import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, Loader2, Send } from "lucide-react";

import { Button } from "@/shared/ui/button";
import { Textarea } from "@/shared/ui/textarea";
import { cn } from "@/shared/lib/utils";
import { useConfirm } from "@/shared/hooks/use-confirm";
import { grouped as groupedCredits } from "@/entities/scene-card/model/creditRates";
import {
  posterService,
  type InstructionPreset,
  type PresetCategory,
} from "../../../api/posterService";

const CATEGORY_ORDER: PresetCategory[] = ["style", "color", "elements", "scene"];

/**
 * 重画 Tab:Semantic 重画。计费=按一张出图价带报价单扣,LLM 改写不另收(0926 修复波 P10;
 * 旧说明「1 张图额度 + LLM token」是错的,别照它改回文案)。
 *
 * - V6 fix4 (2026-05-12): chip 改多选 — 用户可以选多个 preset 叠加(如 "更复古"+"蓝色调"
 *   +"海边场景"),前端把选中 preset 的 label 用 "、" 拼成一条 instruction 走 LLM。
 *   原单选模式保留逻辑:仍可只选一个;选 0 个时启用自由文本框。
 * - 自由文本框跟 chip 多选互斥:选了 chip 文本框灰,清空 chip 文本框恢复
 * - 两个按钮:重画并保留 / 重画并替换(替换在 v2.9 暂未生效,告知用户)
 */
export interface RegenerateTabProps {
  busy: boolean;
  onRegenerate: (body: { presetId?: string; instruction?: string; replace: boolean }) => void;
  /**
   * 只有「替换」这一档(2026-08-08,长图分段)。
   *
   * ★长图分段的 seq 是**块序 index+2** 的硬契约 —— 没有第二个槽位放旧版。
   *   照旧渲「重画并保留」等于**界面在说谎**:用户以为原图还在,实际已经被盖掉。
   */
  replaceOnly?: boolean;
  /**
   * 本次要扣的积分(一张出图的钱;修复波 0926)。按钮点下去就真出一张、真扣一笔,
   * 不写数 = 让用户盲扣。价目表没到货时为 null/缺省 —— 按钮上**不出数字**,不出一个可能过期的数
   * (与同页 GroupRedrawPanel 同一写法)。
   */
  credits?: number | null;
}

export function RegenerateTab({ busy, onRegenerate, replaceOnly, credits }: RegenerateTabProps) {
  const costSuffix = credits == null ? "" : ` · ${groupedCredits(credits)} 积分`;
  const { t } = useTranslation("poster");
  const confirm = useConfirm();
  const CATEGORY_LABEL_I18N: Record<PresetCategory, string> = {
    style: t("editor.categoryStyle"),
    color: t("editor.categoryColor"),
    elements: t("editor.categoryElements"),
    scene: t("editor.categoryScene"),
  };
  // V6 fix4: 改成多选 — Set 比 array 操作更简洁
  const [presetIds, setPresetIds] = useState<Set<string>>(new Set());
  const [text, setText] = useState("");

  const presetsQ = useQuery({
    queryKey: ["poster-instruction-presets"],
    queryFn: posterService.listInstructionPresets,
    staleTime: 10 * 60_000,
  });
  const presets: InstructionPreset[] = presetsQ.data?.presets ?? [];

  const grouped = CATEGORY_ORDER.map((cat) => ({
    category: cat,
    items: presets.filter((p) => p.category === cat),
  })).filter((g) => g.items.length > 0);

  const noOption = presetIds.size === 0 && !text.trim();

  function togglePreset(id: string) {
    setPresetIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    if (text) setText("");
  }

  function submit(replace: boolean) {
    if (presetIds.size === 1) {
      // 单选场景 — 用 presetId 走原后端语义(保留 preset.id 关联的 metadata)。
      // ★同时带上 label 作 instruction(LI30,0910):长图「改这一块」的 regen 端点
      //   只认 instruction,此前单选时 presetId 在 SegmentEditorDialog 被丢弃 →
      //   发出去的是**空指令整屏重画**(最易掉字的形态,选两个以上反而正常)。
      //   海报端点 presetId 优先(posterService 注释),多带 instruction 无副作用。
      const [only] = presetIds;
      const label = presets.find((p) => p.id === only)?.label;
      onRegenerate({ presetId: only, instruction: label, replace });
      return;
    }
    if (presetIds.size > 1) {
      // 多选 — 拼 label 成自然语言 instruction 送给 LLM,backend 当成自由指令处理
      const labels = presets.filter((p) => presetIds.has(p.id)).map((p) => p.label);
      onRegenerate({ instruction: labels.join("、"), replace });
      return;
    }
    // 0 选 — 走自由文本
    onRegenerate({ instruction: text.trim() || undefined, replace });
  }

  return (
    <div className="space-y-4 pt-2">
      <div className="flex items-start gap-2 rounded bg-warning/10 p-2 text-xs text-warning">
        <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
        <span>{t("editor.redrawWarn")}</span>
      </div>

      {presetsQ.isLoading ? (
        <div className="flex items-center gap-2 py-4 text-xs text-muted-foreground">
          <Loader2 className="h-3 w-3 animate-spin" /> {t("editor.redrawLoading")}
        </div>
      ) : (
        <div className="space-y-3">
          {grouped.map(({ category, items }) => (
            <section key={category}>
              <div className="mb-1.5 text-2xs text-muted-foreground">
                {CATEGORY_LABEL_I18N[category]}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {items.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    title={p.description}
                    onClick={() => togglePreset(p.id)}
                    className={cn(
                      "rounded-md border px-2.5 py-1 text-xs transition-colors",
                      presetIds.has(p.id)
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-border hover:border-primary/50"
                    )}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      <section className="space-y-1.5">
        <label className="text-2xs text-muted-foreground" htmlFor="regen-text">
          {t("editor.redrawFreeTextLabel")}
        </label>
        <Textarea
          id="regen-text"
          rows={3}
          maxLength={400}
          placeholder={t("editor.redrawPlaceholder")}
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            if (e.target.value.trim()) setPresetIds(new Set());
          }}
          disabled={busy || presetIds.size > 0}
        />
      </section>

      <div className="flex flex-col gap-2 sm:flex-row">
        <Button
          variant="default"
          disabled={busy || noOption}
          onClick={async () => {
            if (replaceOnly) {
              // 单槽位:重画就覆盖,没有退路 —— 先问一句。
              const ok = await confirm({
                title: "重画会覆盖这一块",
                description: "长图的每一块只存一版,重画完拿不回上一版。确定要重画吗?",
                confirmText: "重画这一块",
                variant: "destructive",
              });
              if (!ok) return;
            }
            submit(replaceOnly === true);
          }}
          className="flex-1"
        >
          {busy ? (
            <>
              <Loader2 className="mr-1 h-3 w-3 animate-spin" /> {t("editor.redrawRunning")}
            </>
          ) : (
            <>
              <Send className="mr-1 h-3 w-3" />{" "}
              {replaceOnly ? "重画这一块" : t("editor.redrawKeep")}
              {costSuffix}
            </>
          )}
        </Button>
        {!replaceOnly && (
          <Button
            variant="outline"
            disabled={busy || noOption}
            onClick={async () => {
              const ok = await confirm({
                title: t("editor.i2iReplaceConfirmTitle"),
                description: t("editor.redrawReplaceConfirmDesc"),
                confirmText: t("editor.i2iReplaceBtn"),
                variant: "destructive",
              });
              if (ok) submit(true);
            }}
            className="flex-1 hover:border-destructive hover:text-destructive"
          >
            {t("editor.redrawReplaceLabel")}
            {costSuffix}
          </Button>
        )}
      </div>
    </div>
  );
}
